import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import {
  createContact,
  fetchContactsByAccountId,
  mapFormDataToApiData,
  ContactFormData,
  deleteContact,
  fetchContactById,
  updateContact,
  fetchTopActiveContactsByAccountId,
  fetchAllContacts,
} from "@/services/frontendServices/contactsServices";
import { ContactDto, ContactWithAuditDto } from "@/types";
import { EditContactFormData } from "@/components/forms/edit-contact-form";
import { accountKeys } from "./useAccounts";
import { activityKeys } from "./useActivities";
import { showErrorToast } from "@/lib/error-handling";

// Keys for queries, to maintain consistency
export const contactKeys = {
  all: ["contacts"] as const,
  lists: () => [...contactKeys.all, "list"] as const,
  list: (accountId: string) => [...contactKeys.lists(), accountId] as const,
  details: () => [...contactKeys.all, "detail"] as const,
  detail: (contactId: string) => [...contactKeys.details(), contactId] as const,
};

/**
 * Invalidates all related caches when a contact is deleted.
 * This ensures activities, related objects, and account data refresh.
 */
const invalidateContactRelatedCaches = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  // Invalidate all activities since they may reference the deleted contact
  queryClient.invalidateQueries({
    queryKey: activityKeys.all,
  });

  // Invalidate related objects that may reference activities with this contact
  const relatedObjectKeys = [
    "signalRelatedObjects",
    "goalRelatedObjects",
    "goalProgress",
    "actionItemRelatedObjects",
    "actionItemProgress",
    "technologyRelatedObjects",
  ];

  relatedObjectKeys.forEach((key) => {
    queryClient.invalidateQueries({
      queryKey: [key],
    });
  });

  // Invalidate all accounts to update last updated dates
  queryClient.invalidateQueries({
    queryKey: accountKeys.all,
  });
};

// Hook to get all contacts for a specific account
export const useContacts = (accountId: string) => {
  return useQuery<ContactDto[], Error>({
    queryKey: contactKeys.list(accountId),
    queryFn: () => fetchContactsByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to get top 2 most active contacts for a specific account
export const useTopActiveContacts = (accountId: string) => {
  return useQuery<ContactDto[], Error>({
    queryKey: [...contactKeys.list(accountId), "topActive"],
    queryFn: () => fetchTopActiveContactsByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to get a specific contact by ID
export const useContact = (contactId: string) => {
  return useQuery<ContactWithAuditDto | null, Error>({
    queryKey: contactKeys.detail(contactId),
    queryFn: () => fetchContactById(contactId),
    enabled: !!contactId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to create new contact
export const useCreateContact = () => {
  const queryClient = useQueryClient();

  return useMutation<ContactWithAuditDto, Error, ContactFormData>({
    mutationFn: async (formData: ContactFormData) => {
      if (!formData.accountId) {
        throw new Error("Account ID is required");
      }
      const apiData = mapFormDataToApiData(formData);
      const newContact = await createContact(formData.accountId, apiData);
      return newContact;
    },
    onSuccess: (newContact, variables) => {
      const accountId = variables.accountId;
      if (accountId) {
        // Invalidate the account's contacts list query to force a refetch
        queryClient.invalidateQueries({
          queryKey: contactKeys.list(accountId),
        });

        // Also invalidate the top active contacts cache
        // This ensures the Key Contacts section updates when new contacts are created
        queryClient.invalidateQueries({
          queryKey: [...contactKeys.list(accountId), "topActive"],
        });

        // Refresh any cross-account contact views (e.g., All Contacts page)
        queryClient.invalidateQueries({
          queryKey: contactKeys.all,
        });

        // Invalidate account cache to update last updated date
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });

        toast.success("Contact saved successfully!", {
          description: `Contact has been created.`,
          duration: 3000,
        });
      }
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating contact",
        defaultMessage: "Failed to create contact",
        duration: 5000,
      });
    },
  });
};

// Hook to update an existing contact
export const useUpdateContact = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ContactWithAuditDto,
    Error,
    { contactId: string; data: Partial<EditContactFormData> }
  >({
    mutationFn: async ({ contactId, data }) => {
      // For updates, we send the data directly since EditContactFormData doesn't include accountId
      const updatedContact = await updateContact(contactId, data);
      return updatedContact;
    },
    onSuccess: (updatedContact, { contactId }) => {
      // Update the contact detail cache
      queryClient.setQueryData<ContactWithAuditDto>(
        contactKeys.detail(contactId),
        updatedContact
      );

      const accountId = updatedContact.account?.id;
      if (accountId) {
        // Update the contact in the cached list for its account
        queryClient.setQueryData<ContactDto[]>(
          contactKeys.list(accountId),
          (oldData = []) =>
            oldData.map((contact) =>
              contact.id === contactId ? updatedContact : contact
            )
        );
        // Invalidate the list query to ensure the list is up-to-date
        queryClient.invalidateQueries({
          queryKey: contactKeys.list(accountId),
        });

        // Invalidate account cache to update last updated date
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });
      } else {
        logger.warn(
          "Account ID not found on updated contact. Consider broader invalidation or ensure account.id is populated on the DTO.",
          { context: { source: "service", module: "useContacts" } }
        );
        // Invalidate all contact list queries as a less targeted fallback.
        queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      }
      toast.success("Contact updated successfully!", {
        description: `Contact "${updatedContact.name} ${updatedContact.lastname}" has been updated.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error updating contact",
        defaultMessage: "Failed to update contact",
        duration: 5000,
      });
    },
  });
};

/**
 * Hook to delete an existing contact.
 * Invalidates all related caches including activities, related objects, and account data.
 */
export const useDeleteContact = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { contactId: string }>({
    mutationFn: async ({ contactId }) => {
      await deleteContact(contactId);
    },
    onSuccess: (_, { contactId }) => {
      // Remove the contact from the detail cache
      queryClient.removeQueries({
        queryKey: contactKeys.detail(contactId),
      });

      // Invalidate all contact lists (this covers all account lists and top active contacts)
      queryClient.invalidateQueries({
        queryKey: contactKeys.all,
      });

      // Invalidate all related caches (activities, related objects, accounts)
      invalidateContactRelatedCaches(queryClient);

      toast.success("Contact deleted successfully!", {
        description: "The contact has been deleted.",
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error deleting contact",
        defaultMessage: "Failed to delete contact",
        duration: 5000,
      });
    },
  });
};

// Hook to get all contacts across all accounts for global dashboard statistics
export const useAllContacts = (
  options?: Omit<UseQueryOptions<ContactDto[], Error>, "queryKey" | "queryFn">
) => {
  return useQuery<ContactDto[], Error>({
    queryKey: [...contactKeys.all, "global"],
    queryFn: () => fetchAllContacts(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    ...options,
  });
};
